import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCommentDots, faTimes, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp, faTelegram } from '@fortawesome/free-brands-svg-icons';
import Button from './Button';

const ChatBotContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
`;

const ChatBotButton = styled.button`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: var(--color-primary);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  box-shadow: var(--shadow-md);
  font-size: 1.5rem;
  transition: transform var(--transition-normal), background-color var(--transition-normal);
  z-index: 1002;
  
  &:hover {
    transform: scale(1.1);
    background-color: #1976d2;
  }
`;

const ChatBotPanel = styled(motion.div)`
  position: absolute;
  bottom: 75px;
  right: 0;
  width: 320px;
  background-color: white;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  z-index: 1001;
`;

const ChatBotHeader = styled.div`
  background-color: var(--color-primary);
  color: white;
  padding: var(--spacing-md);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ChatBotTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ChatBotContent = styled.div`
  padding: var(--spacing-md);
`;

const ChatBotMessage = styled.p`
  margin-bottom: var(--spacing-lg);
  line-height: 1.5;
`;

const SocialButtons = styled.div`
  display: flex;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-md);
`;

const SocialButton = styled(Button)`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const ChatForm = styled.form`
  display: flex;
  gap: var(--spacing-sm);
`;

const ChatInput = styled.input`
  flex: 1;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  font-family: var(--font-main);
  
  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

const SendButton = styled.button`
  background-color: var(--color-primary);
  color: white;
  border: none;
  border-radius: var(--border-radius-md);
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  
  &:hover {
    background-color: #1976d2;
  }
`;

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  
  const toggleChat = () => {
    setIsOpen(!isOpen);
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (message.trim()) {
      // Здесь можно добавить логику для отправки сообщения
      console.log('Сообщение отправлено:', message);
      setMessage('');
    }
  };
  
  return (
    <ChatBotContainer>
      <ChatBotButton onClick={toggleChat}>
        <FontAwesomeIcon icon={isOpen ? faTimes : faCommentDots} />
      </ChatBotButton>
      
      <AnimatePresence>
        {isOpen && (
          <ChatBotPanel
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ duration: 0.3 }}
          >
            <ChatBotHeader>
              <ChatBotTitle>Нужна помощь?</ChatBotTitle>
              <CloseButton onClick={toggleChat}>
                <FontAwesomeIcon icon={faTimes} />
              </CloseButton>
            </ChatBotHeader>
            
            <ChatBotContent>
              <ChatBotMessage>
                Здравствуйте! Напишите нам, если у вас есть вопросы о нашей продукции или вы хотите сделать заказ. Мы ответим в ближайшее время!
              </ChatBotMessage>
              
              <SocialButtons>
                <SocialButton
                  variant={'outlined'}
                  size={'small'}
                  as="a"
                  href="https://wa.me/74951339612"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FontAwesomeIcon icon={faWhatsapp} />
                  WhatsApp
                </SocialButton>
                
                <SocialButton
                  variant={'outlined'}
                  size={'small'}
                  as="a"
                  href="https://t.me/kupitstreych"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FontAwesomeIcon icon={faTelegram} />
                  Telegram
                </SocialButton>
              </SocialButtons>
              
              <ChatForm onSubmit={handleSubmit}>
                <ChatInput
                  type="text"
                  placeholder="Напишите сообщение..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
                <SendButton type="submit">
                  <FontAwesomeIcon icon={faPaperPlane} />
                </SendButton>
              </ChatForm>
            </ChatBotContent>
          </ChatBotPanel>
        )}
      </AnimatePresence>
    </ChatBotContainer>
  );
};

export default ChatBot; 